import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { MediaStore, useMediaLibrary, useMediaFolders, type MediaAsset } from "@/lib/admin/media/store";
import { uploadMediaBatch, uploadMediaFile, type BatchResult } from "@/lib/admin/media/upload";
import { PageHeader } from "@/components/admin/PageHeader";
import { logAction } from "@/lib/admin/audit/log";

export const Route = createFileRoute("/admin/media")({ component: MediaLibraryPage });

function MediaLibraryPage() {
  const assets = useMediaLibrary();
  const folders = useMediaFolders();
  const [folder, setFolder] = useState<string>("/");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string } | null>(null);
  const [report, setReport] = useState<BatchResult | null>(null);

  const filtered = useMemo(() => {
    return assets.filter((a) =>
      (folder === "/" ? true : a.folder === folder) &&
      (query.trim() === "" || a.filename.toLowerCase().includes(query.toLowerCase()) || a.alt.toLowerCase().includes(query.toLowerCase()))
    );
  }, [assets, folder, query]);

  const upload = async (files: FileList | null) => {
    const list = files ? Array.from(files) : [];
    if (list.length === 0) return;
    setReport(null);
    setProgress({ done: 0, total: list.length, current: list[0].name });
    const result = await uploadMediaBatch(list, folder, {
      onProgress: (done, total, current) => setProgress({ done, total, current }),
      onUploaded: async (file, uploaded) => {
        const asset = await MediaStore.upload({
          filename: file.name,
          mime: file.type || "application/octet-stream",
          size: file.size,
          url: uploaded.url,
          storagePath: uploaded.path,
          width: uploaded.width,
          height: uploaded.height,
          folder,
        });
        logAction({ entity: "media", entityId: asset.id, action: "upload", summary: `Uploaded ${file.name}` });
      },
    });
    setProgress(null);
    setReport(result);
  };

  const removeSelected = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} asset(s)?`)) return;
    await MediaStore.removeMany([...selected]);
    logAction({ entity: "media", action: "delete", summary: `Deleted ${selected.size} asset(s)` });
    setSelected(new Set());
  };

  const moveSelected = async (dest: string) => {
    if (selected.size === 0) return;
    await MediaStore.moveMany([...selected], dest);
    logAction({ entity: "media", action: "move", summary: `Moved ${selected.size} to ${dest}` });
  };

  const addFolder = () => {
    const name = prompt("New folder name (e.g. /banners)");
    if (!name) return;
    MediaStore.createFolder(name);
  };

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Media library" subtitle={`${assets.length} assets in library`} action={
        <div className="flex items-center gap-2">
          <button onClick={addFolder} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest">New folder</button>
          <button onClick={() => fileRef.current?.click()} className="border border-black bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white">Upload</button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            hidden
            onChange={(e) => { void upload(e.target.files); e.target.value = ""; }}
          />
        </div>
      }/>

      {progress && (
        <div className="border border-black/10 bg-white px-4 py-3 text-sm">
          <p className="text-black/70">
            Uploading {progress.done} / {progress.total} — {progress.current}
          </p>
          <div className="mt-2 h-1 w-full bg-black/10">
            <div
              className="h-full bg-[#C6A15B] transition-all"
              style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {report && (
        <div
          className={`border px-4 py-3 text-sm ${report.failed > 0 ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}
        >
          <div className="flex items-center justify-between gap-3">
            <p className="font-medium">
              {report.failed === 0
                ? `Uploaded ${report.succeeded} / ${report.total}`
                : `${report.succeeded} succeeded · ${report.failed} failed`}
            </p>
            <button onClick={() => setReport(null)} className="text-xs uppercase tracking-widest opacity-70 hover:opacity-100">Dismiss</button>
          </div>
          {report.errors.length > 0 && (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
              {report.errors.map((e) => <li key={e.filename}>{e.filename}: {e.message}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
        <aside className="border border-black/10 bg-white p-3 text-sm">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-black/50">Folders</p>
          <ul className="mt-2 flex flex-col gap-1">
            {folders.map((f) => (
              <li key={f}>
                <button onClick={() => setFolder(f)} className={`w-full truncate px-2 py-1 text-left ${folder === f ? "bg-black text-white" : "hover:bg-black/5"}`}>{f}</button>
              </li>
            ))}
          </ul>
        </aside>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search filename or alt" className="border border-black/15 px-3 py-2 text-sm md:w-72"/>
            {selected.size > 0 && (
              <>
                <span className="text-xs text-black/60">{selected.size} selected</span>
                <select onChange={(e) => e.target.value && (moveSelected(e.target.value), (e.target.value = ""))} className="border border-black/15 px-2 py-2 text-xs">
                  <option value="">Move to...</option>
                  {folders.filter((f) => f !== folder).map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <button onClick={removeSelected} className="border border-red-700/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-red-700">Delete</button>
              </>
            )}
          </div>
          {filtered.length === 0
            ? <div className="border border-dashed border-black/15 p-10 text-center text-sm text-black/50">No assets here yet.</div>
            : <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {filtered.map((a) => (
                  <AssetTile key={a.id} asset={a} selected={selected.has(a.id)} onToggle={() => toggle(a.id)}/>
                ))}
              </div>}
        </div>
      </div>
    </div>
  );
}

function AssetTile({ asset, selected, onToggle }: { asset: MediaAsset; selected: boolean; onToggle: () => void }) {
  const replaceRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const doReplace = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const uploaded = await uploadMediaFile(file, asset.folder);
      await MediaStore.replace(asset.id, {
        filename: file.name,
        mime: file.type || asset.mime,
        size: file.size,
        url: uploaded.url,
        storagePath: uploaded.path,
        width: uploaded.width,
        height: uploaded.height,
      });
      logAction({ entity: "media", entityId: asset.id, action: "replace", summary: `Replaced ${asset.filename}` });
    } catch (err) {
      console.error("[media] replace failed:", err);
      setError(err instanceof Error ? err.message : "Replace failed");
    }
  };

  const removeOne = async () => {
    if (!confirm(`Delete ${asset.filename}?`)) return;
    await MediaStore.remove(asset.id);
    logAction({ entity: "media", entityId: asset.id, action: "delete", summary: `Deleted ${asset.filename}` });
  };

  return (
    <div className={`group relative border ${selected ? "border-black" : "border-black/10"} bg-white`}>
      <button onClick={onToggle} className="absolute right-2 top-2 z-10 h-4 w-4 border border-black/50 bg-white" aria-label="Select">
        {selected && <span className="block h-full w-full bg-black"/>}
      </button>
      <div className="aspect-square w-full overflow-hidden bg-black/5">
        {asset.mime.startsWith("image/")
          ? <img src={asset.url} alt={asset.alt || asset.filename} className="h-full w-full object-cover"/>
          : <div className="flex h-full w-full items-center justify-center text-[10px] uppercase text-black/40">{asset.mime}</div>}
      </div>
      <div className="p-2">
        <p className="truncate text-xs font-medium text-black">{asset.filename}</p>
        <p className="text-[10px] text-black/50">{Math.max(1, Math.round(asset.size / 1024))} KB · {asset.folder}</p>
        <input
          className="mt-2 w-full border border-black/10 px-2 py-1 text-[11px]"
          placeholder="Alt text"
          defaultValue={asset.alt}
          onBlur={(e) => {
            if (e.target.value !== asset.alt) void MediaStore.update(asset.id, { alt: e.target.value });
          }}
        />
        <div className="mt-2 flex items-center gap-2">
          <button onClick={() => replaceRef.current?.click()} className="border border-black/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-widest">Replace</button>
          <button onClick={removeOne} className="border border-red-700/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-red-700">Delete</button>
          <input ref={replaceRef} type="file" accept="image/*" hidden onChange={(e) => { void doReplace(e.target.files?.[0]); e.target.value = ""; }}/>
        </div>
        {error && <p className="mt-2 text-[10px] text-red-700">{error}</p>}
      </div>
    </div>
  );
}
